import { clamp } from '../core/math';
import type { Point } from '../core/types';
import type { ColorSelectImage } from './colorSelect';

/** Per-pixel edge strength, 0 flat and 1 the strongest edge in the image. */
export interface EdgeField {
  readonly width: number;
  readonly height: number;
  readonly strength: Float32Array;
}

export interface TraceOptions {
  /** How far off the straight line the path may wander, in pixels. */
  radius: number;
  /** Edge strength below this counts as no edge at all. */
  contrast: number;
}

/**
 * Search cells are capped so that a long drag cannot stall the interaction; past
 * the cap the caller gets a straight segment, which is what Photoshop's magnetic
 * lasso effectively does when you outrun it.
 */
const MAX_SEARCH_CELLS = 250_000;

const NEIGHBOURS: readonly (readonly [number, number, number])[] = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, Math.SQRT2],
  [1, -1, Math.SQRT2],
  [-1, 1, Math.SQRT2],
  [-1, -1, Math.SQRT2],
];

/** Sobel magnitude over luminance, normalised by the strongest edge found. */
export function buildEdgeField(image: ColorSelectImage): EdgeField {
  const { width, height, pixels } = image;
  const luma = new Float32Array(width * height);
  for (let i = 0; i < luma.length; i += 1) {
    const p = i * 4;
    luma[i] = (0.299 * pixels[p] + 0.587 * pixels[p + 1] + 0.114 * pixels[p + 2]) / 255;
  }

  const strength = new Float32Array(width * height);
  let peak = 0;
  const at = (x: number, y: number): number =>
    luma[Math.min(height - 1, Math.max(0, y)) * width + Math.min(width - 1, Math.max(0, x))];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const gx =
        at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1) -
        (at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1));
      const gy =
        at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1) -
        (at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1));
      const magnitude = Math.hypot(gx, gy);
      strength[y * width + x] = magnitude;
      if (magnitude > peak) peak = magnitude;
    }
  }

  if (peak > 0) for (let i = 0; i < strength.length; i += 1) strength[i] /= peak;
  return { width, height, strength };
}

/** Minimal binary heap of (cost, cell) pairs; Dijkstra is the only user. */
class CostQueue {
  private readonly costs: number[] = [];
  private readonly cells: number[] = [];

  get size(): number {
    return this.cells.length;
  }

  push(cost: number, cell: number): void {
    this.costs.push(cost);
    this.cells.push(cell);
    let index = this.cells.length - 1;
    while (index > 0) {
      const parent = (index - 1) >> 1;
      if (this.costs[parent] <= this.costs[index]) break;
      this.swap(parent, index);
      index = parent;
    }
  }

  pop(): number {
    const top = this.cells[0];
    const cost = this.costs.pop();
    const cell = this.cells.pop();
    if (this.cells.length > 0 && cost !== undefined && cell !== undefined) {
      this.costs[0] = cost;
      this.cells[0] = cell;
      let index = 0;
      for (;;) {
        const left = index * 2 + 1;
        const right = left + 1;
        let smallest = index;
        if (left < this.cells.length && this.costs[left] < this.costs[smallest]) smallest = left;
        if (right < this.cells.length && this.costs[right] < this.costs[smallest]) smallest = right;
        if (smallest === index) break;
        this.swap(smallest, index);
        index = smallest;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    [this.costs[a], this.costs[b]] = [this.costs[b], this.costs[a]];
    [this.cells[a], this.cells[b]] = [this.cells[b], this.cells[a]];
  }
}

/**
 * Cheapest path from `from` to `to` that prefers running along edges. The search
 * is confined to the bounding box of the two points grown by `radius`, so the
 * cost only depends on how far the pointer moved, not on the image size.
 */
export function tracePath(
  field: EdgeField,
  from: Point,
  to: Point,
  options: TraceOptions,
): Point[] {
  const { width, height, strength } = field;
  if (width <= 0 || height <= 0) return [];

  const radius = Math.max(1, Math.round(options.radius));
  // The pointer is free to leave the canvas, so every endpoint that reaches a
  // caller as an anchor is pinned inside the field first.
  const startX = clamp(Math.round(from.x), 0, width - 1);
  const startY = clamp(Math.round(from.y), 0, height - 1);
  const endX = clamp(Math.round(to.x), 0, width - 1);
  const endY = clamp(Math.round(to.y), 0, height - 1);

  const minX = Math.max(0, Math.min(startX, endX) - radius);
  const minY = Math.max(0, Math.min(startY, endY) - radius);
  const maxX = Math.min(width - 1, Math.max(startX, endX) + radius);
  const maxY = Math.min(height - 1, Math.max(startY, endY) + radius);
  const boxWidth = maxX - minX + 1;
  const boxHeight = maxY - minY + 1;

  const inside = (x: number, y: number): boolean =>
    x >= minX && x <= maxX && y >= minY && y <= maxY;
  if (boxWidth <= 0 || boxHeight <= 0 || !inside(startX, startY) || !inside(endX, endY)) {
    return [{ x: endX, y: endY }];
  }
  if (boxWidth * boxHeight > MAX_SEARCH_CELLS) return [{ x: endX, y: endY }];

  const cells = boxWidth * boxHeight;
  const costs = new Float32Array(cells).fill(Infinity);
  const previous = new Int32Array(cells).fill(-1);
  const done = new Uint8Array(cells);
  const index = (x: number, y: number): number => (y - minY) * boxWidth + (x - minX);

  const start = index(startX, startY);
  const goal = index(endX, endY);
  costs[start] = 0;

  const queue = new CostQueue();
  queue.push(0, start);

  while (queue.size > 0) {
    const cell = queue.pop();
    if (done[cell]) continue;
    done[cell] = 1;
    if (cell === goal) break;

    const x = minX + (cell % boxWidth);
    const y = minY + Math.floor(cell / boxWidth);
    for (const [dx, dy, step] of NEIGHBOURS) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inside(nx, ny)) continue;
      const next = index(nx, ny);
      if (done[next]) continue;

      const edge = strength[ny * width + nx];
      const followed = edge < options.contrast ? 0 : edge;
      const cost = costs[cell] + step * (1.05 - followed);
      if (cost >= costs[next]) continue;
      costs[next] = cost;
      previous[next] = cell;
      queue.push(cost, next);
    }
  }

  if (!done[goal]) return [{ x: endX, y: endY }];

  // The start cell is left out: it is the anchor the caller already holds. A
  // wire that ends where it began still reports that one point, so callers can
  // always read an endpoint off the result.
  const path: Point[] = [];
  for (let cell = goal; cell !== -1 && cell !== start; cell = previous[cell]) {
    path.push({ x: minX + (cell % boxWidth), y: minY + Math.floor(cell / boxWidth) });
  }
  path.reverse();
  return path.length > 0 ? path : [{ x: endX, y: endY }];
}
